// <file-upload> — light-DOM enhancement of the server-rendered drop zone
// (Components::FileUpload). Picked or dropped files direct-upload to
// ActiveStorage immediately; each file gets a row (cloned from the server's
// <template>) whose progress bar tracks the XHR. On success the row gains a
// hidden input named per the element's `data-name`, carrying the blob signed
// id — the surrounding form therefore posts blob references, never bytes.
//
// The form's submit button is disabled until at least one upload has
// completed and none are in flight (the server enforces the same rule).
// Idempotent under Turbo Drive cache restores: rows and their hidden inputs
// live in the cached HTML and are simply re-adopted; listeners are wired
// through an AbortController and torn down on disconnect.

import { DirectUpload } from "@rails/activestorage";

const STATUS_TONES: Record<string, string> = {
	uploading: "text-white/50",
	complete: "text-positive",
	error: "text-negative",
};

class FileUpload extends HTMLElement {
	#abort?: AbortController;
	#pending = 0;

	connectedCallback() {
		this.#abort?.abort();
		this.#abort = new AbortController();
		const { signal } = this.#abort;

		this.#zone.addEventListener("click", () => this.#input.click(), { signal });
		this.#input.addEventListener(
			"change",
			() => {
				this.#add(this.#input.files);
				this.#input.value = ""; // re-selecting the same file must re-fire change
			},
			{ signal },
		);

		this.#zone.addEventListener(
			"dragover",
			(event) => {
				event.preventDefault();
				this.#zone.setAttribute("data-dragover", "");
			},
			{ signal },
		);
		this.#zone.addEventListener(
			"dragleave",
			() => this.#zone.removeAttribute("data-dragover"),
			{ signal },
		);
		this.#zone.addEventListener(
			"drop",
			(event) => {
				event.preventDefault();
				this.#zone.removeAttribute("data-dragover");
				this.#add(event.dataTransfer?.files ?? null);
			},
			{ signal },
		);

		// Row removal, delegated — rows appear and disappear after wiring.
		this.addEventListener(
			"click",
			(event) => {
				const remove = (event.target as HTMLElement).closest<HTMLElement>(
					"[data-file-upload-remove]",
				);
				if (!remove || remove.closest("file-upload") !== this) return;
				remove.closest("[data-file-upload-row]")?.remove();
				this.#sync();
			},
			{ signal },
		);

		// Belt and braces alongside the disabled button.
		this.#form?.addEventListener(
			"submit",
			(event) => {
				if (this.#pending > 0) event.preventDefault();
			},
			{ signal },
		);

		this.#sync();
	}

	disconnectedCallback() {
		this.#abort?.abort();
	}

	// -- uploading --------------------------------------------------------------

	#add(files: FileList | null) {
		for (const file of files ?? []) this.#upload(file);
	}

	#upload(file: File) {
		const row = this.#appendRow(file);

		if (!this.#accepts(file)) {
			this.#fail(row, "Unsupported file type");
			return;
		}

		this.#pending += 1;
		this.#sync();

		const upload = new DirectUpload(file, this.#uploadUrl, {
			directUploadWillStoreFileWithXHR: (xhr: XMLHttpRequest) => {
				xhr.upload.addEventListener("progress", (event) => {
					if (event.lengthComputable)
						this.#progress(row, event.loaded / event.total);
				});
			},
		});

		upload.create((error, blob) => {
			this.#pending -= 1;
			if (error) {
				this.#fail(row, "Upload failed — remove and retry");
			} else {
				this.#complete(row, String(blob?.signed_id));
			}
			this.#sync();
		});
	}

	#accepts(file: File) {
		const accept = this.#input.accept;
		if (!accept) return true;
		const name = file.name.toLowerCase();
		return accept
			.split(",")
			.some((ext) => name.endsWith(ext.trim().toLowerCase()));
	}

	// -- rows ---------------------------------------------------------------------

	#appendRow(file: File): HTMLElement {
		const fragment = this.#template.content.cloneNode(true) as DocumentFragment;
		const row = find<HTMLElement>(fragment, "[data-file-upload-row]");
		find(row, "[data-file-upload-name]").textContent = file.name;
		find(row, "[data-file-upload-size]").textContent = formatSize(file.size);
		this.#list.append(row);
		this.#progress(row, 0);
		return row;
	}

	#progress(row: HTMLElement, fraction: number) {
		const percent = Math.round(fraction * 100);
		this.#bar(row).style.width = `${percent}%`;
		this.#status(row, "uploading", `Uploading — ${percent}%`);
	}

	#complete(row: HTMLElement, signedId: string) {
		this.#bar(row).style.width = "100%";
		this.#status(row, "complete", "Uploaded");

		const input = document.createElement("input");
		input.type = "hidden";
		input.name = this.#fieldName;
		input.value = signedId;
		row.append(input); // removing the row removes its blob reference with it
	}

	#fail(row: HTMLElement, message: string) {
		this.#bar(row).classList.replace("bg-accent", "bg-negative");
		this.#status(row, "error", message);
	}

	#status(row: HTMLElement, state: string, message: string) {
		row.dataset.state = state;
		const status = find<HTMLElement>(row, "[data-file-upload-status]");
		status.textContent = message;
		status.className = `shrink-0 text-xs ${STATUS_TONES[state]}`;
	}

	#bar(row: HTMLElement) {
		return find<HTMLElement>(row, "[data-file-upload-bar]");
	}

	// Continue only once something is uploaded and nothing is mid-flight.
	#sync() {
		const submit = this.#form?.querySelector<HTMLButtonElement>(
			'button[type="submit"]',
		);
		if (!submit) return;
		const completed = this.querySelectorAll(
			'[data-file-upload-row][data-state="complete"]',
		).length;
		submit.disabled = this.#pending > 0 || completed === 0;
		submit.classList.toggle("opacity-50", submit.disabled);
	}

	// -- lookups ----------------------------------------------------------------

	get #input() {
		return find<HTMLInputElement>(this, "[data-file-upload-input]");
	}
	get #zone() {
		return find<HTMLElement>(this, "[data-file-upload-zone]");
	}
	get #list() {
		return find<HTMLElement>(this, "[data-file-upload-list]");
	}
	get #template() {
		return find<HTMLTemplateElement>(this, "[data-file-upload-row-template]");
	}
	get #form() {
		return this.closest("form");
	}

	get #uploadUrl() {
		return required(this.dataset.directUploadUrl, "data-direct-upload-url");
	}
	get #fieldName() {
		return required(this.dataset.name, "data-name");
	}
}

// The server component guarantees these exist; a missing one is a markup bug
// worth a loud error, not a silent no-op behind a non-null assertion.
function find<T extends Element>(root: ParentNode, selector: string): T {
	const el = root.querySelector<T>(selector);
	if (!el) throw new Error(`<file-upload>: missing ${selector}`);
	return el;
}

function required(value: string | undefined, attribute: string): string {
	if (!value) throw new Error(`<file-upload>: missing ${attribute} attribute`);
	return value;
}

function formatSize(bytes: number) {
	if (bytes < 1024) return `${bytes} B`;
	const units = ["KB", "MB", "GB"];
	let value = bytes;
	let unit = -1;
	do {
		value /= 1024;
		unit += 1;
	} while (value >= 1024 && unit < units.length - 1);
	return `${value.toFixed(value >= 10 ? 0 : 1)} ${units[unit]}`;
}

if (!customElements.get("file-upload")) {
	customElements.define("file-upload", FileUpload);
}
